import { ChangeDetectionStrategy, Component, HostListener, computed, input, output } from '@angular/core';

import { isEmptyButtonLabel } from '@common/helpers/forms';

import type { FormButtonSettingsData, LoginEduAuthorizationButtonLegacy } from '@sites/entity-core';
import type { LoginEduForm } from '@sites/entity-signal-form';

import { SpFormButtonSettingsSignalComponent } from '@components/sp-form-button-settings-signal';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

/**
 * Styling panel for the "log in" modal trigger. Reads and writes
 * `buttons[index].modalTrigger` straight on the widget signal form; the
 * trigger's own `isActive` flag is owned by the main panel, so it is preserved
 * across every write here.
 */
@Component({
    selector: 'web-authorization-trigger-settings',
    templateUrl: './authorization-trigger-settings.component.html',
    styleUrls: ['./authorization-trigger-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFormButtonSettingsSignalComponent],
})
export class AuthorizationTriggerSettingsComponent {
    public readonly form = input.required<LoginEduForm>();
    public readonly index = input.required<number>();

    public readonly saveEvent = output<void>();
    public readonly cancelEvent = output<void>();

    private readonly button = computed<LoginEduAuthorizationButtonLegacy | undefined>(
        () => this.form().buttons().value()[this.index()] as LoginEduAuthorizationButtonLegacy | undefined
    );

    public readonly settings = computed<FormButtonSettingsData | null>(() => this.button()?.modalTrigger ?? null);

    /** Blank trigger text is invalid — Save and the Enter shortcut are gated on it. */
    public readonly isEmptyLabel = computed(() => isEmptyButtonLabel(this.settings()?.label));

    public onSettingsChange(value: FormButtonSettingsData): void {
        const current = this.button();
        if (!current) return;

        // Spread over the current trigger so `isActive` (owned by the main
        // panel's checkbox) survives a styling edit.
        this.form().updateButton(this.index(), { modalTrigger: { ...current.modalTrigger, ...value } });
    }

    public onSave(): void {
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        if (this.isEmptyLabel()) return;

        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }
}
